import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { getEmpresa, getEmpresas, getEjercicios, createEjercicio, deleteEjercicio, getArchivoSumasSaldos, descargarSumasSaldos } from "@/lib/api";
import { downloadBlob, parseBlobError } from "@/lib/utils";
import { notifyDataChanged } from "@/lib/events";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Trash2,
  Upload,
  FileCheck,
  BarChart3,
  FileSpreadsheet,
  ArrowLeft,
  GitCompare,
  AlertCircle,
  Download,
  CheckCircle2,
  Calendar,
  ListFilter,
  ClipboardCheck,
  Users,
} from "lucide-react";
import ReglasAsignacionContent from "@/components/empresa/ReglasAsignacionContent";
import ChecksValidacionContent from "@/components/empresa/ChecksValidacionContent";
import CompartirContent from "@/components/empresa/CompartirContent";

export default function EmpresaDetallePage() {
  const { empresaId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [empresa, setEmpresa] = useState(null);
  const [ejercicios, setEjercicios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newAnio, setNewAnio] = useState(new Date().getFullYear());
  const [myRole, setMyRole] = useState("owner");

  // Tab activa desde URL o por defecto "ejercicios"
  const activeTab = searchParams.get("tab") || "ejercicios";

  const handleTabChange = (value) => {
    setSearchParams({ tab: value });
  };

  useEffect(() => {
    loadData();
  }, [empresaId]);

  const loadData = async () => {
    try {
      const [empresaRes, ejerciciosRes, todasRes] = await Promise.all([
        getEmpresa(empresaId),
        getEjercicios(empresaId),
        getEmpresas(),
      ]);
      setEmpresa(empresaRes.data);
      setEjercicios(ejerciciosRes.data);
      // Determinar my_role mirando la lista unificada (incluye shared).
      const found = (todasRes.data || []).find((e) => e.id === empresaId);
      setMyRole(found?.my_role || "owner");
    } catch (error) {
      toast.error("Error al cargar datos");
      navigate("/empresas");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEjercicio = async (e) => {
    e.preventDefault();
    try {
      const response = await createEjercicio(empresaId, { anio: parseInt(newAnio) });
      toast.success(`Ejercicio ${newAnio} creado`);
      setDialogOpen(false);
      notifyDataChanged("ejercicios");
      // Navegar directamente a importar
      navigate(`/ejercicios/${response.data.id}/importar`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear ejercicio");
    }
  };

  const handleDeleteEjercicio = async (id) => {
    try {
      await deleteEjercicio(id);
      toast.success("Ejercicio eliminado");
      loadData();
      notifyDataChanged("ejercicios");
    } catch (error) {
      toast.error("Error al eliminar ejercicio");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between" data-testid="empresa-info-card">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/empresas")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="page-title">{empresa?.nombre}</h1>
            {empresa?.nif && (
              <p className="text-muted-foreground font-mono text-sm">{empresa.nif}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {ejercicios.length >= 2 && (
            <Button
              variant="outline"
              onClick={() => navigate(`/empresas/${empresaId}/comparativa`)}
              data-testid="comparativa-btn"
            >
              <GitCompare className="h-4 w-4 mr-2" />
              Comparar Años
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className={`grid w-full ${myRole === "owner" ? "grid-cols-4" : "grid-cols-3"} lg:w-auto lg:inline-flex`} data-testid="empresa-tabs">
          <TabsTrigger value="ejercicios" className="gap-2" data-testid="tab-ejercicios">
            <Calendar className="h-4 w-4 hidden sm:block" />
            Ejercicios
          </TabsTrigger>
          <TabsTrigger value="reglas" className="gap-2" data-testid="tab-reglas">
            <ListFilter className="h-4 w-4 hidden sm:block" />
            Reglas de Asignación
          </TabsTrigger>
          <TabsTrigger value="checks" className="gap-2" data-testid="tab-checks">
            <ClipboardCheck className="h-4 w-4 hidden sm:block" />
            Checks
          </TabsTrigger>
          {myRole === "owner" && (
            <TabsTrigger value="compartir" className="gap-2" data-testid="tab-compartir">
              <Users className="h-4 w-4 hidden sm:block" />
              Compartir
            </TabsTrigger>
          )}
        </TabsList>

        {/* Tab Ejercicios */}
        <TabsContent value="ejercicios" className="mt-4">
          <div className="space-y-4">
            {/* Botón Nuevo Ejercicio */}
            <div className="flex justify-end">
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="new-ejercicio-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Nuevo Ejercicio
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Crear Nuevo Ejercicio</DialogTitle>
                    <DialogDescription>
                      Añade un ejercicio contable para {empresa?.nombre}
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateEjercicio} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="anio">Año del ejercicio</Label>
                      <Input
                        id="anio"
                        type="number"
                        data-testid="ejercicio-anio-input"
                        value={newAnio}
                        onChange={(e) => setNewAnio(e.target.value)}
                        min={2000}
                        max={2100}
                      />
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                        Cancelar
                      </Button>
                      <Button type="submit" data-testid="submit-ejercicio-btn">
                        Crear y Continuar
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Empty State */}
            {ejercicios.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <FileSpreadsheet className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="font-heading text-xl font-semibold mb-2">Sin ejercicios</h3>
                  <p className="text-muted-foreground mb-4 text-center max-w-sm">
                    Crea un ejercicio contable para importar tu Sumas y Saldos y generar los estados financieros
                  </p>
                  <Button onClick={() => setDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Crear Primer Ejercicio
                  </Button>
                </CardContent>
              </Card>
            ) : (
              /* Ejercicios List */
              <div className="space-y-4" data-testid="ejercicios-list">
                {ejercicios.map((ejercicio) => (
                  <EjercicioCard
                    key={ejercicio.id}
                    ejercicio={ejercicio}
                    onDelete={() => handleDeleteEjercicio(ejercicio.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Tab Reglas de Asignación */}
        <TabsContent value="reglas" className="mt-4">
          <ReglasAsignacionContent empresaId={empresaId} />
        </TabsContent>

        {/* Tab Checks de Validación */}
        <TabsContent value="checks" className="mt-4">
          <ChecksValidacionContent empresaId={empresaId} />
        </TabsContent>

        {/* Tab Compartir (solo owner) */}
        {myRole === "owner" && (
          <TabsContent value="compartir" className="mt-4">
            <CompartirContent empresaId={empresaId} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function EjercicioCard({ ejercicio, onDelete }) {
  const navigate = useNavigate();
  const [archivoInfo, setArchivoInfo] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const hasData = ejercicio.total_cuentas > 0;
  const hasPendientes = ejercicio.cuentas_pendientes > 0 || ejercicio.cuentas_error > 0;
  const isReady = hasData && !hasPendientes;

  useEffect(() => {
    // Cargar info del archivo si hay datos importados
    if (hasData) {
      getArchivoSumasSaldos(ejercicio.id)
        .then(res => setArchivoInfo(res.data))
        .catch(() => setArchivoInfo(null));
    }
  }, [ejercicio.id, hasData]);

  const handleDownloadArchivo = async () => {
    setDownloading(true);
    try {
      const response = await descargarSumasSaldos(ejercicio.id);
      downloadBlob(response.data, archivoInfo?.filename || `SumasSaldos_${ejercicio.anio}.xlsx`);
      toast.success("Archivo descargado");
    } catch (error) {
      toast.error(await parseBlobError(error, "No se pudo descargar el archivo"));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card className="group" data-testid={`ejercicio-card-${ejercicio.id}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CardTitle className="font-heading text-2xl">{ejercicio.anio}</CardTitle>
            <Badge variant={ejercicio.estado === "FINAL" ? "default" : "secondary"}>
              {ejercicio.estado}
            </Badge>
            {hasData && (
              <>
                {isReady ? (
                  <Badge variant="outline" className="text-emerald-600 border-emerald-600">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Listo
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-amber-600 border-amber-600">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    Pendientes: {ejercicio.cuentas_pendientes + ejercicio.cuentas_error}
                  </Badge>
                )}
                {ejercicio.fecha_importacion && (
                  <span className="text-xs text-muted-foreground ml-2">
                    Importado: {new Date(ejercicio.fecha_importacion).toLocaleDateString('es-ES', { 
                      day: '2-digit', 
                      month: '2-digit', 
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                )}
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="opacity-0 group-hover:opacity-100"
                  data-testid={`delete-ejercicio-${ejercicio.id}`}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Eliminar ejercicio {ejercicio.anio}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Se eliminarán todas las cuentas importadas. Esta acción no se puede deshacer.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={onDelete}
                    className="bg-destructive text-destructive-foreground"
                  >
                    Eliminar
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {hasData ? (
          <div className="space-y-4">
            {/* Stats */}
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Total cuentas</p>
                <p className="font-mono font-semibold">{ejercicio.total_cuentas}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Balance</p>
                <p className="font-mono font-semibold text-emerald-600">{ejercicio.cuentas_balance}</p>
              </div>
              <div>
                <p className="text-muted-foreground">PyG</p>
                <p className="font-mono font-semibold text-blue-600">{ejercicio.cuentas_pyg}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Pendientes</p>
                <p className={`font-mono font-semibold ${ejercicio.cuentas_pendientes > 0 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                  {ejercicio.cuentas_pendientes}
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              {archivoInfo && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadArchivo}
                  disabled={downloading}
                  className="text-blue-600 border-blue-600 hover:bg-blue-50"
                  data-testid={`descargar-archivo-${ejercicio.id}`}
                >
                  <Download className="h-4 w-4 mr-2" />
                  {downloading ? "Descargando..." : "Sumas y Saldos"}
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/ejercicios/${ejercicio.id}/importar`)}
                data-testid={`importar-btn-${ejercicio.id}`}
              >
                <Upload className="h-4 w-4 mr-2" />
                Reimportar
              </Button>
              {hasPendientes && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/ejercicios/${ejercicio.id}/revision`)}
                  className="text-amber-600 border-amber-600 hover:bg-amber-50"
                  data-testid={`revision-btn-${ejercicio.id}`}
                >
                  <FileCheck className="h-4 w-4 mr-2" />
                  Revisar ({ejercicio.cuentas_pendientes + ejercicio.cuentas_error})
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/ejercicios/${ejercicio.id}/balance`)}
                data-testid={`balance-btn-${ejercicio.id}`}
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Balance
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/ejercicios/${ejercicio.id}/pyg`)}
                data-testid={`pyg-btn-${ejercicio.id}`}
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                PyG
              </Button>
              <Button
                size="sm"
                onClick={() => navigate(`/ejercicios/${ejercicio.id}/dashboard`)}
                data-testid={`dashboard-btn-${ejercicio.id}`}
              >
                <BarChart3 className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between py-4">
            <p className="text-muted-foreground">Sin datos importados</p>
            <Button
              onClick={() => navigate(`/ejercicios/${ejercicio.id}/importar`)}
              data-testid={`importar-first-btn-${ejercicio.id}`}
            >
              <Upload className="h-4 w-4 mr-2" />
              Importar Sumas y Saldos
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
